import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X, Pencil, Target } from "lucide-react";
import { Link } from "react-router-dom";

const ISO_CHIPS = [
  ["cl. 4.1 / 4.2", "Context & interested parties", "Captured in SWOT + vision statements"],
  ["cl. 6.1", "Risks & opportunities", "Feeds the Risk Register module"],
  ["cl. 6.2", "Quality objectives", "1-Year Plan goals + KPI Scorecard"],
  ["cl. 9.3", "Management review", "Level-10 minutes in Meeting OS"],
];

const Lbl = ({ children, onEdit, testid }) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{children}</span>
    {onEdit && <button onClick={onEdit} className="text-cyan-700 hover:text-cyan-800 text-xs font-semibold inline-flex items-center gap-1" data-testid={testid}><Pencil size={11} />Edit</button>}
  </div>
);

const KVRow = ({ metrics }) => (
  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2">
    {(metrics || []).map((m, i) => (
      <div key={i}>
        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{m.label}</div>
        <div className="font-mono text-base font-semibold text-slate-900">{m.value || "—"}</div>
      </div>
    ))}
  </div>
);

function ListEditor({ items, setItems, placeholder, max = 10 }) {
  return (
    <div className="space-y-1.5">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input value={v} placeholder={placeholder} onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))} className="rounded-sm h-8 text-sm flex-1" data-testid={`list-item-${i}`} />
          <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
        </div>
      ))}
      {items.length < max && (
        <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setItems([...items, ""])} data-testid="list-add-btn"><Plus size={12} className="mr-1" />Add</Button>
      )}
    </div>
  );
}

function KVEditor({ metrics, setMetrics, max = 6 }) {
  return (
    <div className="space-y-1.5">
      {metrics.map((m, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input value={m.label} placeholder="Label (e.g. Revenue)" onChange={(e) => setMetrics(metrics.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="rounded-sm h-8 text-sm flex-1" data-testid={`kv-label-${i}`} />
          <Input value={m.value} placeholder="Value (e.g. $4.6M)" onChange={(e) => setMetrics(metrics.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} className="rounded-sm h-8 text-sm w-32 font-mono" data-testid={`kv-value-${i}`} />
          <button onClick={() => setMetrics(metrics.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
        </div>
      ))}
      {metrics.length < max && (
        <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setMetrics([...metrics, { label: "", value: "" }])} data-testid="kv-add-btn"><Plus size={12} className="mr-1" />Add number</Button>
      )}
    </div>
  );
}

export default function ManagementVision() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // {section, draft}
  const { data: v } = useQuery({ queryKey: ["mgmt-vision"], queryFn: async () => (await api.get("/mgmt/vision")).data });

  const saveMut = useMutation({
    mutationFn: async (patch) => (await api.patch("/mgmt/vision", patch)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mgmt-vision"] }); setDialog(null); toast.success("Vision updated"); },
    onError: (e) => toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save"),
  });

  const toggleGoal = (idx) => {
    const goals = v.one_year.goals.map((g, i) => (i === idx ? { ...g, done: !g.done } : g));
    saveMut.mutate({ one_year: { ...v.one_year, goals } });
  };

  if (!v) return <div data-testid="management-vision-page" />;
  const d = dialog?.draft;
  const setD = (p) => setDialog((x) => ({ ...x, draft: { ...x.draft, ...p } }));

  return (
    <div data-testid="management-vision-page" className="pb-24">
      <PageHeader
        title="Strategic Vision Planner"
        subtitle={`Vision / Traction Organiser · ${v.updated_at ? `last updated ${new Date(v.updated_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })} by ${v.updated_by}` : "not filled in yet"} · aligned to ISO 9001 cl. 4–6 & 9.3`}
      />
      <div className="px-4 sm:px-6 lg:px-10 space-y-4">
        <div className="grid lg:grid-cols-2 gap-4 items-start">
          <Card className="rounded-sm border-slate-200 shadow-none p-4" data-testid="core-values-card">
            <Lbl testid="edit-values-btn" onEdit={() => setDialog({ section: "values", draft: { core_values: (v.core_values || []).map((c) => ({ ...c })) } })}>Core Values</Lbl>
            {(v.core_values || []).length === 0 && <div className="text-sm text-slate-400">What you hire, fire and praise by — add 3 to 5.</div>}
            {(v.core_values || []).map((c, i) => (
              <div key={i} className="flex gap-3 py-2 border-b border-slate-100 last:border-0">
                <span className="w-6 h-6 bg-slate-800 text-white font-mono text-xs flex items-center justify-center rounded-sm flex-shrink-0">{i + 1}</span>
                <div><div className="font-semibold text-sm text-slate-900">{c.title}</div>{c.desc && <div className="text-xs text-slate-500">{c.desc}</div>}</div>
              </div>
            ))}
            <div className="mt-4">
              <Lbl testid="edit-tenyear-btn" onEdit={() => setDialog({ section: "ten_year", draft: { ...v.ten_year, metrics: v.ten_year.metrics.map((m) => ({ ...m })) } })}>10-Year Target{v.ten_year.target_year ? ` — ${v.ten_year.target_year}` : ""}</Lbl>
              <div className="text-base font-bold text-slate-900 leading-snug" data-testid="tenyear-statement">{v.ten_year.statement || <span className="text-slate-400 font-normal text-sm">Where is CCFI in 10 years? One big, measurable target.</span>}</div>
              <KVRow metrics={v.ten_year.metrics} />
            </div>
          </Card>
          <Card className="rounded-sm border-slate-200 shadow-none p-4" data-testid="traction-card">
            <Lbl testid="edit-threeyear-btn" onEdit={() => setDialog({ section: "three_year", draft: { ...v.three_year, metrics: v.three_year.metrics.map((m) => ({ ...m })), bullets: [...v.three_year.bullets] } })}>3-Year Picture{v.three_year.date ? ` — ${new Date(v.three_year.date + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}` : ""}</Lbl>
            <KVRow metrics={v.three_year.metrics} />
            <div className="mt-2">
              {(v.three_year.bullets || []).map((b, i) => (
                <div key={i} className="flex items-center gap-2 py-1 border-b border-slate-100 text-sm text-slate-700 last:border-0"><span className="w-1 h-1 bg-cyan-600 flex-shrink-0" />{b}</div>
              ))}
              {(v.three_year.bullets || []).length === 0 && (v.three_year.metrics || []).length === 0 && <div className="text-sm text-slate-400">What does CCFI look like in 3 years?</div>}
            </div>
            <div className="mt-4">
              <Lbl testid="edit-oneyear-btn" onEdit={() => setDialog({ section: "one_year", draft: { metrics: v.one_year.metrics.map((m) => ({ ...m })), goals: v.one_year.goals.map((g) => ({ ...g })) } })}>1-Year Plan</Lbl>
              <KVRow metrics={v.one_year.metrics} />
              <div className="mt-2">
                {(v.one_year.goals || []).map((g, i) => (
                  <label key={g.id || i} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm cursor-pointer last:border-0" data-testid={`year-goal-${i}`}>
                    <input type="checkbox" checked={!!g.done} onChange={() => toggleGoal(i)} className="w-4 h-4 accent-emerald-600" data-testid={`year-goal-check-${i}`} />
                    <span className={g.done ? "line-through text-slate-400" : "text-slate-800"}>{g.title}</span>
                  </label>
                ))}
                {(v.one_year.goals || []).length === 0 && <div className="text-sm text-slate-400">3–7 goals that make this year a win.</div>}
                <Link to="/management/goals" className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-cyan-700 hover:underline" data-testid="vision-goals-link">
                  <Target size={12} />Cascade these key numbers into KPI targets → Annual Goals
                </Link>
              </div>
            </div>
          </Card>
        </div>

        <Card className="rounded-sm border-slate-200 shadow-none p-4" data-testid="swot-card">
          <Lbl testid="edit-swot-btn" onEdit={() => setDialog({ section: "swot", draft: Object.fromEntries(Object.entries(v.swot).map(([k, arr]) => [k, arr.join("\n")])) })}>SWOT Analysis — reviewed quarterly</Lbl>
          <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {[["strengths", "Strengths", "text-emerald-600"], ["weaknesses", "Weaknesses", "text-red-600"], ["opportunities", "Opportunities", "text-cyan-700"], ["threats", "Threats", "text-amber-600"]].map(([k, label, cls]) => (
              <div key={k} className="bg-white p-3" data-testid={`swot-${k}`}>
                <div className={`text-[11px] uppercase tracking-[0.14em] font-bold mb-1.5 ${cls}`}>{label}</div>
                {(v.swot[k] || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-2 py-0.5 text-xs text-slate-700"><span className="w-1 h-1 bg-slate-300 mt-1.5 flex-shrink-0" />{item}</div>
                ))}
                {(v.swot[k] || []).length === 0 && <div className="text-xs text-slate-300">—</div>}
              </div>
            ))}
          </div>
        </Card>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="iso-strip">
          {ISO_CHIPS.map(([c, t, d2]) => (
            <div key={c} className="bg-white border border-slate-200 border-l-[3px] border-l-cyan-600 rounded-sm px-3 py-2.5">
              <div className="font-mono text-[10px] text-cyan-700 font-semibold">ISO 9001 · {c}</div>
              <div className="font-semibold text-xs text-slate-900 mt-0.5">{t}</div>
              <div className="text-[11px] text-slate-500">{d2}</div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="vision-dialog">
          <DialogHeader><DialogTitle>
            {{ values: "Core Values", ten_year: "10-Year Target", three_year: "3-Year Picture", one_year: "1-Year Plan", swot: "SWOT Analysis" }[dialog?.section]}
          </DialogTitle></DialogHeader>

          {dialog?.section === "values" && d && (
            <div className="space-y-2">
              {d.core_values.map((c, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1 space-y-1">
                    <Input value={c.title} placeholder="Value — e.g. Safety before schedule" onChange={(e) => setD({ core_values: d.core_values.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} className="rounded-sm h-8 text-sm" data-testid={`value-title-${i}`} />
                    <Input value={c.desc || ""} placeholder="One-line meaning" onChange={(e) => setD({ core_values: d.core_values.map((x, j) => (j === i ? { ...x, desc: e.target.value } : x)) })} className="rounded-sm h-8 text-xs" data-testid={`value-desc-${i}`} />
                  </div>
                  <button onClick={() => setD({ core_values: d.core_values.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600 mt-2"><X size={14} /></button>
                </div>
              ))}
              {d.core_values.length < 8 && <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setD({ core_values: [...d.core_values, { title: "", desc: "" }] })} data-testid="add-value-btn"><Plus size={12} className="mr-1" />Add value</Button>}
            </div>
          )}

          {dialog?.section === "ten_year" && d && (
            <div className="space-y-3">
              <Textarea rows={3} value={d.statement} placeholder="The one big target — e.g. The Coast's #1 commercial fencing contractor, $15M revenue, zero LTIs" onChange={(e) => setD({ statement: e.target.value })} className="rounded-sm" data-testid="tenyear-input" />
              <Input value={d.target_year} placeholder="Target year (e.g. 2036)" onChange={(e) => setD({ target_year: e.target.value })} className="rounded-sm w-44 font-mono" data-testid="tenyear-year-input" />
              <KVEditor metrics={d.metrics} setMetrics={(m) => setD({ metrics: m })} />
            </div>
          )}

          {dialog?.section === "three_year" && d && (
            <div className="space-y-3">
              <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Picture date</label>
                <Input type="date" value={d.date} onChange={(e) => setD({ date: e.target.value })} className="rounded-sm mt-1 w-44" data-testid="threeyear-date-input" /></div>
              <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Key numbers</label>
                <div className="mt-1"><KVEditor metrics={d.metrics} setMetrics={(m) => setD({ metrics: m })} max={8} /></div></div>
              <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">What it looks like</label>
                <div className="mt-1"><ListEditor items={d.bullets} setItems={(b) => setD({ bullets: b })} placeholder="e.g. Fabrication shop on 2 shifts" max={8} /></div></div>
            </div>
          )}

          {dialog?.section === "one_year" && d && (
            <div className="space-y-3">
              <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Key numbers</label>
                <div className="mt-1"><KVEditor metrics={d.metrics} setMetrics={(m) => setD({ metrics: m })} /></div></div>
              <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Goals for the year</label>
                <div className="mt-1 space-y-1.5">
                  {d.goals.map((g, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input type="checkbox" checked={!!g.done} onChange={(e) => setD({ goals: d.goals.map((x, j) => (j === i ? { ...x, done: e.target.checked } : x)) })} className="w-4 h-4 accent-emerald-600" />
                      <Input value={g.title} placeholder="Goal" onChange={(e) => setD({ goals: d.goals.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} className="rounded-sm h-8 text-sm flex-1" data-testid={`goal-title-${i}`} />
                      <button onClick={() => setD({ goals: d.goals.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
                    </div>
                  ))}
                  {d.goals.length < 10 && <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setD({ goals: [...d.goals, { title: "", done: false }] })} data-testid="add-goal-btn"><Plus size={12} className="mr-1" />Add goal</Button>}
                </div></div>
            </div>
          )}

          {dialog?.section === "swot" && d && (
            <div className="grid grid-cols-2 gap-3">
              {[["strengths", "Strengths"], ["weaknesses", "Weaknesses"], ["opportunities", "Opportunities"], ["threats", "Threats"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label>
                  <Textarea rows={5} value={d[k]} placeholder="One per line" onChange={(e) => setD({ [k]: e.target.value })} className="rounded-sm mt-1 text-xs" data-testid={`swot-input-${k}`} />
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog(null)} data-testid="cancel-vision-btn">Cancel</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" disabled={saveMut.isPending} data-testid="save-vision-btn"
              onClick={() => {
                const s = dialog.section;
                if (s === "values") saveMut.mutate({ core_values: d.core_values.filter((c) => c.title.trim()) });
                else if (s === "swot") saveMut.mutate({ swot: Object.fromEntries(Object.entries(d).map(([k, txt]) => [k, txt.split("\n").map((x) => x.trim()).filter(Boolean).slice(0, 10)])) });
                else if (s === "one_year") saveMut.mutate({ one_year: { metrics: d.metrics.filter((m) => m.label.trim()), goals: d.goals.filter((g) => g.title.trim()) } });
                else if (s === "ten_year") saveMut.mutate({ ten_year: { ...d, metrics: d.metrics.filter((m) => m.label.trim()) } });
                else saveMut.mutate({ three_year: { ...d, metrics: d.metrics.filter((m) => m.label.trim()), bullets: d.bullets.map((b) => b.trim()).filter(Boolean) } });
              }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
